import React from 'react';
import { useTranslation } from 'react-i18next';
import './PaymentErrorModal.css';

const PaymentErrorModal = ({
  isOpen,
  error,
  onClose,
  onRetry,
  onChangePaymentMethod,
  onContactSupport,
  reservationAmount
}) => {
  const { t } = useTranslation('paymentErrors');
  
  if (!isOpen) return null;

  // Extraire les détails de l'erreur Stripe
  const getErrorDetails = (error) => {
    let stripeCode = null;
    let rawError = error;

    // Si c'est une erreur structurée du backend
    if (typeof error === 'object' && error.lastPaymentError) {
      stripeCode = error.lastPaymentError.code || error.lastPaymentError.decline_code;
      rawError = error.lastPaymentError;
    }
    // Si c'est une erreur avec message contenant du JSON
    else if (typeof error === 'string' && error.includes('lastPaymentError')) {
      try {
        const match = error.match(/\{.*lastPaymentError.*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          stripeCode = parsed.lastPaymentError?.code || parsed.lastPaymentError?.decline_code;
          rawError = parsed.lastPaymentError;
        }
      } catch (e) {
        console.warn('Could not parse error details:', e);
      }
    }
    // Si c'est une erreur simple avec code
    else if (typeof error === 'object' && error.code) {
      stripeCode = error.code;
    }

    return {
      code: stripeCode,
      raw: rawError,
      message: typeof error === 'string' ? error : error.message
    };
  };

  const errorDetails = getErrorDetails(error);
  
  // Déterminer le type d'erreur et récupérer les traductions
  const errorType = errorDetails.code || 'default';
  const errorConfig = {
    title: t(`errors.${errorType}.title`, t('errors.default.title')),
    message: t(`errors.${errorType}.message`, t('errors.default.message'))
  };

  // Actions disponibles selon le type d'erreur
  const getActionsForError = (errorType) => {
    switch (errorType) {
      case 'card_declined':
        return ['retry', 'change_card'];
      case 'insufficient_funds':
        return ['change_card', 'contact_bank'];
      case 'expired_card':
        return ['change_card'];
      case 'incorrect_cvc':
        return ['retry'];
      case 'processing_error':
        return ['retry', 'contact_support'];
      case 'rate_limit':
        return ['wait', 'contact_support'];
      case 'authentication_required':
        return ['retry', 'contact_bank'];
      case 'generic_decline':
        return ['change_card', 'contact_bank'];
      default:
        return ['retry', 'contact_support'];
    }
  };

  // Déterminer la sévérité pour le style
  const getSeverity = (errorType) => {
    switch (errorType) {
      case 'insufficient_funds':
      case 'expired_card':
      case 'generic_decline':
        return 'error';
      case 'authentication_required':
        return 'info';
      default:
        return 'warning';
    }
  };

  const actions = getActionsForError(errorType);
  const severity = getSeverity(errorType);

  const handleAction = (action) => {
    switch (action) {
      case 'retry':
        onRetry?.();
        break;
      case 'change_card':
        onChangePaymentMethod?.();
        break;
      case 'contact_support':
        onContactSupport?.();
        break;
      case 'contact_bank':
        // Action informative, fermer le modal
        onClose();
        break;
      case 'wait':
        // Action informative, fermer le modal
        onClose();
        break;
      default:
        onClose();
    }
  };

  return (
    <div className="payment-error-modal-overlay">
      <div className="payment-error-modal">
        <div className={`error-header ${severity}`}>
          <h2>{errorConfig.title}</h2>
          <button 
            className="close-button"
            onClick={onClose}
            aria-label={t('ui.closeButton')}
          >
            ✕
          </button>
        </div>

        <div className="error-content">
          <div className="error-message">
            <p>{errorConfig.message}</p>
          </div>

          {reservationAmount && (
            <div className="payment-details">
              <div className="amount-reminder">
                <strong>{t('ui.amountToPay', { amount: reservationAmount })}</strong>
              </div>
            </div>
          )}

          <div className="error-actions">
            {actions.map((action, index) => (
              <button
                key={action}
                className={`action-button ${action === 'retry' ? 'primary' : 'secondary'}`}
                onClick={() => handleAction(action)}
              >
                {t(`actions.${action}`)}
              </button>
            ))}
          </div>

          {/* Détails techniques pour debug (collapsible) */}
          <details className="technical-details">
            <summary>{t('ui.technicalDetails')}</summary>
            <pre className="error-raw">
              {JSON.stringify({
                code: errorDetails.code,
                timestamp: new Date().toISOString(),
                raw: errorDetails.raw
              }, null, 2)}
            </pre>
          </details>
        </div>

        <div className="error-footer">
          <p className="help-text">
            {t('ui.helpText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentErrorModal;

